/** Bonuses admin page (Phase 4.4). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Gift } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

type TxnType = "earn_purchase" | "spend_purchase" | "expire";

interface BonusTxn {
  id: number;
  client_id: number;
  type: TxnType;
  amount: number;
  balance_after: number;
  order_id: number | null;
  description: string | null;
  expires_at: string | null;
  is_expired: boolean;
  created_at: string;
}

interface TxnsResponse {
  data: BonusTxn[];
  pagination: { total: number };
}

interface BonusAccount {
  client_id: number;
  balance: number;
  total_earned: number;
  total_spent: number;
  updated_at: string;
}

function formatSum(tiyin: number): string {
  return (tiyin / 100).toLocaleString("ru-RU");
}

export function BonusesPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [clientIdFilter, setClientIdFilter] = useState("");
  const [grantOpen, setGrantOpen] = useState(false);

  const { data: txns, isLoading: txnsLoading } = useQuery({
    queryKey: ["admin-bonus-transactions", clientIdFilter],
    queryFn: async () => {
      const params: Record<string, unknown> = { per_page: 100 };
      if (clientIdFilter.trim()) params.client_id = Number(clientIdFilter);
      const { data } = await api.get<TxnsResponse>("/bonus-transactions", {
        params,
      });
      return data;
    },
  });

  const { data: account } = useQuery({
    queryKey: ["admin-bonus-account", clientIdFilter],
    enabled: !!clientIdFilter.trim(),
    queryFn: async () => {
      const { data } = await api.get<BonusAccount>(
        `/bonus-accounts/${Number(clientIdFilter)}`,
      );
      return data;
    },
  });

  const items = txns?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-heading">{t("nav.bonuses")}</h1>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">{t("bonuses.clientIdFilter")}</Label>
            <Input
              type="number"
              value={clientIdFilter}
              onChange={(e) => setClientIdFilter(e.target.value)}
              placeholder={t("bonuses.allClients")}
              className="w-48"
            />
          </div>
          <Button onClick={() => setGrantOpen(true)}>
            <Gift className="w-4 h-4 mr-1" />
            {t("bonuses.grantTitle")}
          </Button>
        </div>
      </div>

      {grantOpen && (
        <GrantForm
          defaultClientId={clientIdFilter}
          onClose={() => setGrantOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["admin-bonus-transactions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-bonus-account"] });
          }}
        />
      )}

      {account && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("bonuses.accountTitle")} #{account.client_id}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <Stat label={t("bonuses.balance")} value={formatSum(account.balance)} />
            <Stat
              label={t("bonuses.totalEarned")}
              value={formatSum(account.total_earned)}
            />
            <Stat
              label={t("bonuses.totalSpent")}
              value={formatSum(account.total_spent)}
            />
          </CardContent>
        </Card>
      )}

      {txnsLoading ? (
        <Skeleton className="h-96" />
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          {t("bonuses.empty")}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("bonuses.when")}</TableHead>
                  <TableHead>{t("bonuses.client")}</TableHead>
                  <TableHead>{t("bonuses.type")}</TableHead>
                  <TableHead className="text-right">{t("bonuses.amount")}</TableHead>
                  <TableHead className="text-right">
                    {t("bonuses.balanceAfter")}
                  </TableHead>
                  <TableHead>{t("bonuses.context")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString("ru-RU")}
                    </TableCell>
                    <TableCell>
                      <a
                        href={`/clients/${row.client_id}`}
                        className="text-primary hover:underline"
                      >
                        #{row.client_id}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.type === "earn_purchase"
                            ? "default"
                            : row.type === "spend_purchase"
                            ? "secondary"
                            : "destructive"
                        }
                      >
                        {t(`bonuses.types.${row.type}`)}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums font-medium ${
                        row.amount > 0
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {row.amount > 0 ? "+" : ""}
                      {formatSum(row.amount)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatSum(row.balance_after)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.order_id && (
                        <a
                          href={`/orders/${row.order_id}`}
                          className="text-primary hover:underline mr-2"
                        >
                          #{row.order_id}
                        </a>
                      )}
                      {row.description ?? ""}
                      {row.expires_at && !row.is_expired && (
                        <span className="block">
                          {t("bonuses.expiresAt")}:{" "}
                          {new Date(row.expires_at).toLocaleDateString("ru-RU")}
                        </span>
                      )}
                      {row.is_expired && (
                        <Badge variant="outline" className="text-xs">
                          {t("bonuses.expired")}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function GrantForm({
  defaultClientId,
  onClose,
  onSuccess,
}: {
  defaultClientId: string;
  onClose: () => void;
  onSuccess: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    client_id: defaultClientId,
    amount_sum: 0, // в сумах, конвертация ×100 при отправке
    description: "",
    expires_at: "",
  });

  const grantMutation = useMutation({
    mutationFn: async () => {
      await api.post("/bonus-transactions/grant", {
        client_id: Number(form.client_id),
        amount: Math.round(Number(form.amount_sum) * 100),
        description: form.description || null,
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : null,
      });
    },
    onSuccess: () => {
      toast.success(t("bonuses.granted"));
      onSuccess();
      onClose();
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message ?? t("common.toast.errorOccurred")),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("bonuses.grantTitle")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>{t("bonuses.client")} ID</Label>
            <Input
              type="number"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              min={1}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("bonuses.amountSum")}</Label>
            <Input
              type="number"
              value={form.amount_sum}
              onChange={(e) =>
                setForm({ ...form, amount_sum: Number(e.target.value) })
              }
              min={1}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>{t("bonuses.description")}</Label>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            placeholder={t("bonuses.descriptionPlaceholder")}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("bonuses.expiresAt")}</Label>
          <Input
            type="date"
            value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => grantMutation.mutate()}
            disabled={
              grantMutation.isPending ||
              !form.client_id ||
              form.amount_sum <= 0
            }
          >
            {t("bonuses.grantButton")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
